import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.min.css';
import { ToastContainer, toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { expiryMsgStore, showExpiryToast } from './services/Createapiinstance';
import Cookies from 'js-cookie';
import './App.css';
 
import { addLog } from './store/auth-store';
import TokenService from './services/token.service';
import { MenuSettingsProvider } from './components/MenuSettingsContext';
import { PermissionsProvider } from './shared/Permissions/PermissionsContext';
import { LoadingProvider } from './shared/Loader/LoadingContext';
import GlobalLoader from './shared/Loader/Loader';
import { SoftLoadingProvider } from './shared/Loader/SoftLoadingContext';
import SoftLoader from './shared/Loader/SoftLoader';
import SidebarNew from './components/SidebarNew';
import ScrollToTop from './ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import FormSubmissionHeader from './screens/FormSubmissions/Formheader';
import { isKrishiDeletionRoute } from './KrishiDeletion/config';
 
// ─── Eagerly-loaded screens (tiny / needed before auth resolves) ──────────────
import LoginScreen from './screens/Login/loginScreen';
import ForgotPassword from './screens/ForgotPassword/forgotPassword';
import ResetPassword from './screens/ResetPassword/ResetPassword';
// ─── Lazily-loaded screens (code-split; loaded only when the route is visited) ─
const NewDashboardView            = lazy(() => import('./screens/Dashboard/DashBoardView/NewDashboardView'));
const UserMasterOverView          = lazy(() => import('./screens/UserMaster/UserMasterOverView'));
const MasterDataUploadOverView    = lazy(() => import('./screens/MasterDataUploadNew/MasterDataUpload'));
const MasterDataUploadList        = lazy(() => import('./screens/MasterDataUploadNew/MasterDataUploadList'));
const WorkflowOverView            = lazy(() => import('./screens/Workflow/WorkflowOverview'));
const TaskOverView                = lazy(() => import('./screens/Workflow/TaskOverview'));
const FilterOverview              = lazy(() => import('./screens/FilterSettings/FilterOverView'));
const GeneralSettings             = lazy(() => import('./screens/GeneralSettings/GeneralSettings'));
const ScheduledApis               = lazy(() => import('./screens/ScheduledApis/ScheduledApis'));
const Integrations                = lazy(() => import('./screens/Integrations/Integrations'));
const AllocationOverview          = lazy(() => import('./screens/Allocation/AllocationOverView'));
const OrganizationOverview        = lazy(() => import('./screens/Organization/OrganizationOverview'));
const OrganizationProfile         = lazy(() => import('./screens/Organization/OrganizationProfile'));
const OrganizationAnalytics       = lazy(() => import('./screens/Organization/OrganizationAnalytics'));
const PlatformAnalytics           = lazy(() => import('./screens/Dashboard/PlatformAnalytics'));
const NormalTaskOverview          = lazy(() => import('./screens/NormalTaskCreation/NormalTaskOverview'));
const ProjectHomeOverView         = lazy(() => import('./screens/Project/ProjectHomeOverView'));
const SurveyOverView              = lazy(() => import('./screens/Survey/SurveyOverview'));
const FormsOverView               = lazy(() => import('./screens/DynamicForm/DynamicFormOverView'));
const SubFormsOverView            = lazy(() => import('./screens/SubForm/SubFormOverView'));
const FormBuilderOverview         = lazy(() => import('./screens/FormBuilder/FormBuilderOverView'));
const FormSubmissionNewComponent  = lazy(() => import('./screens/FormSubmissions/FormSubmissionNew'));
const FormSubmissionOverview      = lazy(() => import('./screens/FormSubmissions/FormSubmissionOverview'));
const WorkflowTaskOverView        = lazy(() => import('./screens/Allocation/WorkflowTaskOverView'));
const RolesandPermissionOverview  = lazy(() => import('./screens/RolesandPermission/RolesPermissionOverview'));
const ActionBuilderBuilderOverview= lazy(() => import('./screens/ActionTable/ActionTableOverview'));
const ActionTableBuilder          = lazy(() => import('./screens/ActionTable/ActionTableBuilder'));
const DataUpload                  = lazy(() => import('./screens/DataUpload/Dataupload'));
const FormBuilderComponent        = lazy(() => import('./screens/FormBuilder/FormBuilder'));
const FormGroupBuilder            = lazy(() => import('./screens/FormGroup/FormGroupBuilder'));
const FormGroupOverview           = lazy(() => import('./screens/FormGroup/FormGroupOverview'));
const ActionTableList             = lazy(() => import('./screens/FormSubmissions/ActionTableList'));
const MyExports                   = lazy(() => import('./screens/Exports/MyExports'));
const MenuComponent               = lazy(() => import('./screens/MenuOrder/MenuOrder'));
const DynamicApiOverview          = lazy(() => import('./screens/DynamicApi/DynamicApiList'));
const AuditLog                    = lazy(() => import('./screens/Audit-log/Audit-log'));
const DashboardList               = lazy(() => import('./screens/Dashboard/DashboardList'));
const DashboardBuilderPreview     = lazy(() => import('./screens/Dashboard/DashBoardBuilderPreview'));
const DashboardBuilder            = lazy(() => import('./screens/Dashboard/DashboardBuilder'));
const ReportView                  = lazy(() => import('./screens/Report/ReportView'));
const PdfBuilderListing           = lazy(() => import('./screens/PdfBuilder/PdfBuilderListing'));
const PdfBuilderEditor            = lazy(() => import('./screens/PdfBuilder/PdfBuilderEditor'));
const ApiBuilderScreen            = lazy(() => import('./screens/ApiBuilder/ApiBuilderScreen'));
const ApiEditScreen               = lazy(() => import('./screens/ApiBuilder/ApiEditScreen'));

// Custom forms
const PurchaseOrder               = lazy(() => import('./screens/CustomForms/Efl custom forms/PurchaseOrder'));
const IssueOfItems                = lazy(() => import('./screens/CustomForms/Efl custom forms/IssueOfItems'));
const GoodsReceiptNote            = lazy(() => import('./screens/CustomForms/Efl custom forms/GoodsReceiptNote'));
const ApprovalRequest             = lazy(() => import('./screens/CustomForms/Efl custom forms/ApprovalRequest'));
const FarmerContract              = lazy(() => import('./screens/CustomForms/AvtCustomForms/FarmerContract'));
const FAContract                  = lazy(() => import('./screens/CustomForms/AvtCustomForms/FAContract'));
const DigilockerCallback          = lazy(() => import('./screens/CustomForms/AvtCustomForms/DigilockerCallback'));
const DigilockerThankYou          = lazy(() => import('./screens/CustomForms/AvtCustomForms/DigilockerThankYou'));
const Staff                       = lazy(() => import('./screens/CustomForms/AvtCustomForms/Staff'));
const TransportContract           = lazy(() => import('./screens/CustomForms/AvtCustomForms/TransportContract'));
const LandLeaseAgreement          = lazy(() => import('./screens/CustomForms/AvtCustomForms/LandLeaseAgreement'));
const KrishiDeletionApp           = lazy(() => import('./KrishiDeletion'));
 
// Static Suspense fallback — always shows the spinner during chunk loading
// regardless of isGlobalLoading state (context-dependent GlobalLoader would
// render nothing when isGlobalLoading=false, causing a blank screen mid-nav).
const SuspenseFallback = () => (
  <div className="gl-overlay" role="status" aria-label="Loading">
    <div className="gl-content">
      <div className="gl-dots" aria-hidden="true">
        <span className="gl-dot" /><span className="gl-dot" /><span className="gl-dot" />
      </div>
    </div>
  </div>
);

// Paths that force the sidebar into collapsed mode
const FORCE_COLLAPSE_PREFIXES = [
    '/formbuilder',
    '/formgroupbuilder',
    '/pdf-builder/',   // collapse for the editor (/pdf-builder/:id), not the listing
];
const FORCE_COLLAPSE_EXCEPT = [
    '/actiontable_builder_overview',
    '/dashboard_builder_overview',
];
const FORCE_COLLAPSE_PREFIXES_WITH_EXCEPT = [
    '/actiontable_builder',
    '/dashboard_builder',
];
 
function shouldForceCollapse(pathname) {
    if (FORCE_COLLAPSE_PREFIXES.some((p) => pathname.startsWith(p))) return true;
    return FORCE_COLLAPSE_PREFIXES_WITH_EXCEPT.some(
        (p) => pathname.startsWith(p) && !FORCE_COLLAPSE_EXCEPT.some((e) => pathname.startsWith(e))
    );
}
 
function App() {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isDimmed, setIsDimmed] = useState(false);
    const [isZohoDashboard, setIsZohoDashboard] = useState(false);
 
    const dispatch = useDispatch();
    const loginStatus = useSelector((state) => state.login.currentStatus.status);
    const location = useLocation();
 
    // Re-decrypt on login/logout (loginStatus flips on both — addLog(true)/addLog(false))
    // rather than once on mount: login/logout both navigate client-side (no full
    // reload), so this component never remounts between sessions — a stale memo
    // here previously meant logging in as one role right after a different role's
    // session could keep showing the old role's cookie value until a hard refresh.
    // Keyed on loginStatus rather than location.pathname so this only re-decrypts
    // at the two moments the cookies can actually change, not on every navigation.
    const userData        = useMemo(() => TokenService.decryptData(Cookies.get('userdetails')), [loginStatus]);
    // org_id sentinel for the platform superadmin (matches backend's requireSuperAdmin) —
    // separate from userData, which is the account's username and shouldn't be used for this check.
    const orgId            = useMemo(() => TokenService.decryptData(Cookies.get('org_id')), [loginStatus]);
    const disabledashboard = useMemo(() => TokenService.decryptData(Cookies.get('disabledashboard')), [loginStatus]);
 
    // Sync Redux auth state with cookie on mount — never during render.
    useEffect(() => {
        dispatch(addLog(!!userData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally runs once on mount only
 
    // Forced password reset — LoginScreen leaves a flag in sessionStorage when
    // the login response has mustResetPassword; we show the popup here, on top
    // of the dashboard, once the user is logged in.
    // const [showForceReset, setShowForceReset] = useState(false);
    // useEffect(() => {
    //     if (!loginStatus) {
    //         setShowForceReset(false);
    //         return;
    //     }
    //     if (sessionStorage.getItem('mustResetPassword') !== 'true') return;
    //     // Short delay so the dashboard paints before the popup opens.
    //     const timer = setTimeout(() => setShowForceReset(true), 800);
    //     return () => clearTimeout(timer);
    // }, [loginStatus]);

    // const closeForceReset = () => {
    //     sessionStorage.removeItem('mustResetPassword');
    //     setShowForceReset(false);
    // };

    // Collapse sidebar on builder routes
    useEffect(() => {
        if (shouldForceCollapse(location.pathname)) {
            setIsSidebarCollapsed(true);
        }
    }, [location.pathname]);

    // Show expiry message every 60s — interceptor handles first show on header detection
    useEffect(() => {
        if (!loginStatus) return;
        const interval = setInterval(() => {
            if (expiryMsgStore.current) {
                showExpiryToast(expiryMsgStore.current);
            }
        }, 900 * 1000);
        return () => clearInterval(interval);
    }, [loginStatus]);
 
    // Use location from React Router — do not read window.location directly.
    const showFormHeader =
        loginStatus && location.pathname.startsWith('/form_submissions');

    // ── Krishi account-deletion page ─────────────────────────────────────────
    // A standalone page published as the account deletion URL on the Krishi
    // Play Store listing. It renders instead of the whole FlowForms tree — no
    // sidebar, no providers, no routes — because a user arriving from that
    // listing must not be able to reach any FlowForms screen. See
    // KrishiDeletion/README.md.
    //
    // This sits BELOW every hook above and ABOVE the return on purpose. An
    // early return placed before the hooks would change how many hooks run
    // between renders the moment the pathname changed, which React rejects
    // ("rendered fewer hooks than expected"). Here the hook order is identical
    // on every path.
    if (isKrishiDeletionRoute(location.pathname)) {
        return (
            <Suspense fallback={<SuspenseFallback />}>
                <KrishiDeletionApp />
            </Suspense>
        );
    }

    return (
        <PermissionsProvider>
            <div className="app-div-container">
                <LoadingProvider>
                  <SoftLoadingProvider>
                    <div className={loginStatus ? 'main-content-area' : ''}>
                        <ToastContainer
                            position="top-center"
                            closeButton={false}
                            hideProgressBar
                            autoClose={1000}
                            limit={1}
                        />
                        <MenuSettingsProvider>
                            <ScrollToTop />
                            <GlobalLoader />
                            <SoftLoader />
 
                            <div className={loginStatus ? 'sidebar-menu' : 'sidebar-menu hidden'}>
                                {loginStatus && (
                                    <SidebarNew
                                        collapsed={isSidebarCollapsed}
                                        setCollapsed={setIsSidebarCollapsed}
                                        onMenuClick={(menuItem) => {
                                            if (menuItem?.toLowerCase() !== 'home') {
                                                setIsDimmed(true);
                                            }
                                        }}
                                        onMenuClose={() => setIsDimmed(false)}
                                    />
                                )}
                            </div>
 
                            {loginStatus && (
                                <div className={`screen-dim-overlay ${isDimmed ? 'active' : ''}`} />
                            )}
 
                            {showFormHeader && (
                                <div className="form-page-wrapper">
                                    <FormSubmissionHeader />
                                </div>
                            )}
 
                            <div
                                className={loginStatus ? 'main-content' : ''}
                                style={
                                    loginStatus
                                        ? {
                                              marginLeft: isSidebarCollapsed
                                                  ? 'var(--sidebar-collapsed-width)'
                                                  : 'var(--sidebar-width)',
                                              transition: 'margin-left 0.3s ease',
                                              backgroundColor: 'var(--background-color)',
                                              marginBottom: 'unset',
                                          }
                                        : undefined
                                }
                            >
                                <ErrorBoundary>
                                <Suspense fallback={<SuspenseFallback />}>
                                    <Routes>
                                        {!loginStatus ? (
                                            <>
                                                <Route path="/login" element={<LoginScreen />} />
                                                <Route path="/forgot_password" element={<ForgotPassword />} />
                                                <Route path="*" element={<LoginScreen />} />
                                            </>
                                        ) : (
                                            <>
                                                <Route
                                                    path="/"
                                                    element={
                                                        <Navigate
                                                            to="/dashboard"
                                                            replace
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path="/dashboard"
                                                    element={
                                                        orgId === 'superAdmin' ? (
                                                            <PlatformAnalytics />
                                                        ) : (
                                                            <NewDashboardView
                                                                collapsed={isSidebarCollapsed}
                                                                isDimmed={isDimmed}
                                                                disabledashboard={disabledashboard}
                                                                setIsZohoDashboard={setIsZohoDashboard}
                                                            />
                                                        )
                                                    }
                                                />
                                                <Route path="/newSidebar"                            element={<SidebarNew />} />
                                                <Route path="/users"                                 element={<UserMasterOverView />} />
                                                <Route path="/masterdatas"                           element={<MasterDataUploadOverView />} />
                                                <Route path="/masterdatas/:id"                       element={<MasterDataUploadList />} />
                                                <Route path="/workflow"                              element={<WorkflowOverView />} />
                                                <Route path="/workflow_tasks"                        element={<TaskOverView />} />
                                                <Route path="/formsubmission_new/:action/:id/:form_id" element={<FormSubmissionNewComponent />} />
                                                <Route path="/formsubmission_new/:action/:id"        element={<FormSubmissionNewComponent />} />
                                                <Route path="/formsub/:id"                           element={<FormSubmissionOverview collapsed={isSidebarCollapsed} />} />
                                                <Route path="/filtersettings"                        element={<FilterOverview />} />
                                                <Route path="/surveyallocations"                     element={<AllocationOverview />} />
                                                <Route path="/task_creation"                         element={<NormalTaskOverview />} />
                                                <Route path="/projects"                              element={<ProjectHomeOverView collapsed={isSidebarCollapsed} />} />
                                                <Route path="/data-upload/:formId/:formName"         element={<DataUpload />} />
                                                <Route path="/survey/:id"                            element={<SurveyOverView />} />
                                                <Route path="/formList/:id"                          element={<FormsOverView />} />
                                                <Route path="/subformList/:id"                       element={<SubFormsOverView />} />
                                                <Route path="/formBuilderOverview/:id"               element={<FormBuilderOverview />} />
                                                <Route path="/actiontable_builder_overview/:id"      element={<ActionBuilderBuilderOverview />} />
                                                <Route path="/organizations"                         element={<ProtectedRoute element={<OrganizationOverview />} requiredRole="superadmin" />} />
                                                <Route path="/organizations/:id"                     element={<ProtectedRoute element={<OrganizationProfile />} requiredRole="superadmin" />} />
                                                <Route path="/organizations/:id/analytics"           element={<ProtectedRoute element={<OrganizationAnalytics />} requiredRole="superadmin" />} />
                                                <Route path="/reset_password"                        element={<ResetPassword />} />
                                                <Route path="/form_workflow_task_details/:id"        element={<WorkflowTaskOverView />} />
                                                <Route path="/roles"                                 element={<RolesandPermissionOverview />} />
                                                <Route path="/formbuilder/:id/:subform"              element={<FormBuilderComponent />} />
                                                <Route path="/formbuilder/:id"                       element={<FormBuilderComponent />} />
                                                <Route path="/form_group/:id"                        element={<FormGroupOverview />} />
                                                <Route path="/formgroupbuilder/:id"                  element={<FormGroupBuilder />} />
                                                <Route path="/actiontable_builder/:id"               element={<ActionTableBuilder />} />
                                                <Route path="/actiontable_list/:id"                  element={<ActionTableList />} />
                                                <Route path="/my-exports"                            element={<MyExports />} />
                                                <Route path="/report_list/:id"                       element={<ReportView />} />
                                                <Route path="/pdf-builder"                           element={<PdfBuilderListing />} />
                                                <Route path="/pdf-builder/:id"                       element={<PdfBuilderEditor />} />
                                                <Route path="/api-builder"                           element={<ApiBuilderScreen />} />
                                                <Route path="/api-builder/new"                       element={<ApiEditScreen />} />
                                                <Route path="/api-builder/edit/:id"                  element={<ApiEditScreen />} />
                                                <Route path="/menu_order"                            element={<MenuComponent />} />
                                                <Route path="/audit-log"                             element={<AuditLog />} />
                                                <Route path="/generalsettings"                       element={<GeneralSettings />} />
                                                <Route path="/dynamic_apis"                          element={<DynamicApiOverview />} />
                                                <Route path="/scheduledapis"                         element={<ScheduledApis />} />
                                                <Route path="/integrations"                          element={<Integrations />} />
                                                <Route path="/dashboard_list"                        element={<DashboardList />} />
                                                <Route path="/dashboard_builder_overview/:id"        element={<DashboardBuilderPreview />} />
                                                <Route path="/dashboard_builder/:id"                 element={<DashboardBuilder />} />
 
                                                {/* Custom forms — EFL */}
                                                <Route path="/purchaseorder"                         element={<PurchaseOrder />} />
                                                <Route path="/issueofitems"                          element={<IssueOfItems />} />
                                                <Route path="/goodsreceipt"                          element={<GoodsReceiptNote />} />
                                                <Route path="/approvalrequest/:action/:id"           element={<ApprovalRequest />} />
                                                <Route path="/approvalrequest/:action/:id/:form_id/:uuid" element={<ApprovalRequest />} />
 
                                                {/* Custom forms — AVT */}
                                                <Route path="/farmercontract"                        element={<FarmerContract />} />
                                                <Route path="/facontract"                            element={<FAContract />} />
                                                <Route path="/digilocker"                            element={<DigilockerCallback />} />
                                                <Route path="/digilockerthankyou"                    element={<DigilockerThankYou />} />
                                                <Route path="/staff"                                 element={<Staff />} />
                                                <Route path="/transportcontract"                     element={<TransportContract />} />
                                                <Route path="/landleaseagreement"                     element={<LandLeaseAgreement />} />
                                                {/* Catch-all: redirects /login and any unknown path to dashboard */}
                                                <Route
                                                    path="*"
                                                    element={
                                                        <Navigate
                                                            to="/dashboard"
                                                            replace
                                                        />
                                                    }
                                                />
                                            </>
                                        )}
                                    </Routes>
                                    {/* {loginStatus && showForceReset && (
                                        <ResetPassword
                                            open={showForceReset}
                                            onClose={closeForceReset}
                                            onSaved={closeForceReset}
                                        />
                                    )} */}
                                </Suspense>
                                </ErrorBoundary>
                            </div>
                        </MenuSettingsProvider>
                    </div>
                  </SoftLoadingProvider>
                </LoadingProvider>
            </div>
        </PermissionsProvider>
    );
}
 
export default App;